import CargoCharacteristicEntity from 'Models/Entities/CargoCharacteristicEntity';
import { CargoCharacteristicDto, EffectiveCargoCharacteristicDto } from './CargoDtos';

/**
 * Merge a side's own effective characteristics with the *linked* characteristics from the other
 * side. A characteristic flagged `appliesToLinkedTrailerVehicle` carries between a vehicle and its
 * trailer, so it should display on both. Duplicates are tolerated — CargoCharacteristicTags
 * de-dupes by id when rendering.
 */
export function withLinkedCharacteristics(
	own: EffectiveCargoCharacteristicDto[] | undefined,
	other: EffectiveCargoCharacteristicDto[] | undefined,
	cargoCharacteristicMap: Record<string, CargoCharacteristicDto>,
): EffectiveCargoCharacteristicDto[] {
	const linkedFromOther = (other ?? []).filter(c => cargoCharacteristicMap[c.id]?.appliesToLinkedTrailerVehicle);
	return [...(own ?? []), ...linkedFromOther];
}

/**
 * The characteristics that actually apply to a vehicle or trailer being booked: the ones inherited
 * from its cargo / tow-on type, plus the ones ticked on this booking, minus the inherited ones the
 * customer or staff removed. A removal always wins.
 *
 * The clientside mirror of the server's `ComputeEffectiveCharacteristics`. Ids only — callers that
 * need names already have the characteristic map.
 */
export function getEffectiveCharacteristicIds(
	inheritedIds: string[] | undefined,
	selected: { characteristicId: string }[] | undefined,
	removedInherited: { characteristicId: string }[] | undefined,
): string[] {
	const removed = new Set((removedInherited ?? []).map(x => x.characteristicId));
	const effective = new Set<string>();
	for (const id of inheritedIds ?? []) {
		if (!removed.has(id)) effective.add(id);
	}
	for (const { characteristicId } of selected ?? []) {
		if (!removed.has(characteristicId)) effective.add(characteristicId);
	}
	return [...effective];
}

export interface CargoCharacteristicLike {
	name: string;
	selectableLabelTransalationId?: string | null;
}

/**
 * Generates the translation ID for a cargo characteristic.
 * Uses the explicit translation ID if available, otherwise falls back to a convention-based ID.
 * @param characteristic The characteristic object containing name and optional translation ID.
 */
export function getCargoCharacteristicTranslationId(characteristic: CargoCharacteristicLike): string {
	return characteristic.selectableLabelTransalationId
		? characteristic.selectableLabelTransalationId
		: `characteristic.${characteristic.name}.name`;
}

export function mapCargoCharacteristicEntityToDto(entity: CargoCharacteristicEntity): CargoCharacteristicDto {
	return {
		id: entity.id,
		name: entity.name,
		displayName: entity.displayName,
		abbreviation: entity.abbreviation,
		iconName: entity.iconName,
		showToCustomer: entity.showToCustomer ?? false,
		showToStaff: entity.showToStaff ?? false,
		appliesToLinkedTrailerVehicle: entity.appliesToLinkedTrailerVehicle ?? false,
		isSelectableForVehicle: entity.isSelectableForVehicle ?? false,
		isSelectableForTrailer: entity.isSelectableForTrailer ?? false,
		selectableLabelTransalationId: entity.selectableLabelTransalationId,
	};
}
